/* ═════════════════════════════ how it looks ═════════════════════════════

   Templates and design overrides: nine whole looks for the app, each one a
   stylesheet that is inert until `data-template` on <html> matches it, plus
   the handful of things a stylesheet cannot own — the note palette, which is
   read by JavaScript that paints canvas and inline styles, and the overrides
   the user sets by hand.

   The one thing worth knowing: changing the template has to repaint the
   thoughts board, because notes carry inline colours chosen from the palette
   rather than classes a stylesheet could restyle. The board lives elsewhere,
   so this module does not reach for it — it calls a handler installed by
   whoever owns both. Importing the board here would make a cycle out of what
   is really a notification. */

import { $, el, toast, setDrawer, cssVar, cssVarHex } from './dom.js';
import { S, save } from '../state/store.js';
import {
  DESIGN_DEFAULTS, DESIGN_FIELDS, FONT_CHOICES,
  NOTE_PALETTE, BAUHAUS_NOTE_PALETTE, SWISS_NOTE_PALETTE, BLANK_NOTE_PALETTE,
  NEUMORPH_NOTE_PALETTE, GLASS_NOTE_PALETTE, SKETCH_NOTE_PALETTE,
  MONOCHROME_NOTE_PALETTE, DARKGLOW_NOTE_PALETTE, SPRIGHTLY_NOTE_PALETTE,
} from '../core/palette.js';
import { exportAll, backupFilename, inspectBackup, restoreBackup, describeBackup } from '../state/backup.js';
import { storage, KEYS } from '../state/storage.js';
import { askConfirm } from './dom.js';

/** Called after the look changes, so whoever owns the board can repaint it. */
let onRepaint = () => {};

/** @param {() => void} fn */
export function onLookChanged(fn) { onRepaint = fn; }

export const TEMPLATES = [
  {
    key: 'blank', corners: 'round', name: 'Blank', notes: BLANK_NOTE_PALETTE,
    blurb: 'No opinion. System type, plain surfaces, no ornament.',
    swatch: ['#ffffff', '#f1f3f5', '#8b95a1', '#2f3437'],
    confetti: ['#5b6b7c', '#7d8b99', '#9aa6b2', '#4a5560', '#b0bac4', '#6a7684'],
  },
  {
    key: 'paper', corners: 'round', name: 'Paper', notes: NOTE_PALETTE,
    blurb: 'Cream notebook, fountain-pen ink, soft edges.',
    swatch: ['#faf4e4', '#3d63b6', '#1c7a5b', '#a9701a'],
    confetti: ['#3d63b6', '#1c7a5b', '#a9701a', '#7250cd', '#bd463f', '#1a7a91'],
  },
  {
    key: 'bauhaus', corners: 'square', name: 'Bauhaus', notes: BAUHAUS_NOTE_PALETTE,
    blurb: 'Poster geometry — Dessau, 1919. Circles, hard ink, primaries.',
    swatch: ['#efe7d5', '#d8402a', '#f2a81d', '#1b44a0'],
    confetti: ['#d8402a', '#f2a81d', '#1b44a0', '#2e8a83', '#14120f', '#c08a2e'],
  },
  {
    key: 'neumorphism', corners: 'round', name: 'Neumorphism', notes: NEUMORPH_NOTE_PALETTE,
    blurb: 'Soft UI. One colour, shape from light alone, everything extruded.',
    swatch: ['#e4e9f2', '#c8d0e0', '#5b7cfa', '#2c3444'],
    confetti: ['#5b7cfa', '#a06bfa', '#4a9b77', '#c99a3a', '#d4626e', '#8a94ab'],
  },
  {
    key: 'glassmorphism', corners: 'round', name: 'Glassmorphism', notes: GLASS_NOTE_PALETTE,
    blurb: 'Frosted panes over colour. Backdrop blur and a lit top edge.',
    swatch: ['#dfe7fb', '#a0beff', '#e282dc', '#78e1d7'],
    confetti: ['#4560d8', '#b04ad0', '#20a882', '#e0a92b', '#d94a63', '#7896ff'],
  },
  {
    key: 'swiss', corners: 'square', name: 'International Typographic', notes: SWISS_NOTE_PALETTE,
    blurb: 'Basel, 1957. Strict grid, flush left, one red, nothing else.',
    swatch: ['#ffffff', '#e02200', '#1c1c1c', '#9b9b9b'],
    confetti: ['#e02200', '#1c1c1c', '#9b9b9b', '#0b5fa5', '#e8b923', '#4a4a4a'],
  },
  {
    /* the only template that asks for neither round nor square corners — see the
       data-corners='wobbly' blanket in styles.css for why that is an attribute
       rather than forty-five overrides */
    key: 'sketch', corners: 'wobbly', name: 'Sketch', notes: SKETCH_NOTE_PALETTE,
    blurb: 'Notebook paper, felt-tip, cut-out cards, nothing straight.',
    swatch: ['#fdfbf7', '#2d2d2d', '#d8382f', '#2d5da1'],
    confetti: ['#d8382f', '#2d5da1', '#2f7d4f', '#a9701a', '#2d2d2d', '#fff3ae'],
  },
  {
    key: 'monochrome', corners: 'square', name: 'Monochrome', notes: MONOCHROME_NOTE_PALETTE,
    blurb: 'Editorial black and white. Serif display, rules, no colour.',
    swatch: ['#ffffff', '#000000', '#525252', '#e5e5e5'],
    /* even the celebration is monochrome — a burst of colour here would undo the
       one rule the whole template is built on */
    confetti: ['#000000', '#525252', '#8a8a8a', '#c0c0c0', '#1a1a1a', '#6e6e6e'],
  },
  {
    key: 'darkglow', corners: 'round', name: 'Dark Glow', notes: DARKGLOW_NOTE_PALETTE,
    blurb: 'Near-black panes, indigo light pools, lit glass edges.',
    swatch: ['#050506', '#5E6AD2', '#A78BFA', '#0f0f12'],
    confetti: ['#5E6AD2', '#A78BFA', '#35C99B', '#E0A63F', '#F2686B', '#8A8F98'],
  },
  {
    key: 'sprightly', corners: 'round', name: 'Sprightly', notes: SPRIGHTLY_NOTE_PALETTE,
    blurb: 'Memphis pop. Pill shapes, chunky ink, hard shadows.',
    swatch: ['#FFFDF5', '#7C3AED', '#F472B6', '#FBBF24'],
    confetti: ['#7C3AED', '#F472B6', '#FBBF24', '#34D399', '#1E293B', '#DB2777'],
  },
];

export const templateOf = (key) => TEMPLATES.find((t) => t.key === key) || TEMPLATES[1];
export const activeTemplate = () => templateOf(S.ui && S.ui.template);

/* ── advanced design: the user's overrides on top of a template ──

   Every one of these lands as an inline custom property on <html>, and an
   inline declaration outranks every stylesheet rule — which is what lets a
   slider beat a template without either knowing about the other. `null` means
   "whatever the template says", so a template switch keeps working underneath
   any override the user has not touched.

   `fallback` is the token a template is expected to define, so the settings
   panel can show what the current template's own answer is. */

export const fontChoice = (key) => FONT_CHOICES.find((f) => f.key === key) || null;

export function applyDesign() {
  const st = document.documentElement.style;
  const d = S.design;

  const set = (prop, val) => (val == null || val === '' ? st.removeProperty(prop) : st.setProperty(prop, val));

  set('--ui-heading', d.headingFont ? (fontChoice(d.headingFont) || {}).css : null);
  set('--ui-body',    d.bodyFont    ? (fontChoice(d.bodyFont)    || {}).css : null);
  set('--accent',     d.accent);
  // one slider drives both radii, the small one tracking at two thirds
  set('--radius',     d.radius == null ? null : d.radius + 'px');
  set('--radius-sm',  d.radius == null ? null : Math.round(d.radius * 0.66) + 'px');
  set('--ui-ornament', d.ornament == null ? null : String(d.ornament));
}

/** True when the user has overridden anything — drives the Reset button. */
export const designIsCustom = () => DESIGN_FIELDS.some((f) => S.design[f.key] != null);

/** Repaint the app under `key`. `paint` is false at boot, where the usual
    render pass is about to run anyway. */
export function applyTemplate(key, paint = true) {
  const tpl = templateOf(key);
  S.ui.template = tpl.key;
  save();

  document.documentElement.dataset.template = tpl.key;
  /* Corner intent is a separate attribute because it is enforced by ONE shared
     rule in styles.css rather than by each template restating ~45 selectors.
     The base sheet hardcodes a literal radius in about that many places and
     those literals ignore --radius, so a square template cannot get there by
     token alone. */
  document.documentElement.dataset.corners = tpl.corners || 'round';
  applyDesign();
  renderSettings();

  // notes and their colour legend are the JS-coloured part of the app
  if (paint) onRepaint();
}

/* ── the settings drawer ── */

/** Rebuild the drawer's contents from state. Cheap, and called after every
    change so the template cards and the override controls never disagree. */
export function renderSettings() {
  const tpl = activeTemplate();

  /* template cards */
  const list = $('tplList');
  if (list.childElementCount !== TEMPLATES.length) {
    list.textContent = '';
    TEMPLATES.forEach((t) => {
      const b = el('button', 'tpl-item');
      b.dataset.tpl = t.key;
      b.setAttribute('role', 'radio');

      const sw = el('span', 'tpl-swatch');
      t.swatch.forEach((c) => { const i = el('i'); i.style.background = c; sw.appendChild(i); });

      const txt = el('span');
      txt.appendChild(el('span', 'tpl-name', t.name));
      txt.appendChild(el('span', 'tpl-note', t.blurb));

      b.append(sw, txt);
      b.addEventListener('click', () => {
        applyTemplate(t.key);
        toast(`${t.name} template`);
      });
      list.appendChild(b);
    });
  }
  [...list.children].forEach((b) => {
    const on = b.dataset.tpl === tpl.key;
    b.classList.toggle('is-active', on);
    b.setAttribute('aria-checked', String(on));
  });

  /* the override controls, and what the template's own answer is */
  const d = S.design;
  $('dsHeading').value = d.headingFont || '';
  $('dsBody').value    = d.bodyFont || '';
  $('dsAccent').value  = d.accent || cssVarHex('--accent');
  $('dsAccentUsing').textContent = d.accent ? 'custom' : 'from template';

  const radius = d.radius == null ? parseInt(cssVar('--radius'), 10) || 0 : d.radius;
  $('dsRadius').value = radius;
  $('dsRadiusOut').textContent = radius + 'px' + (d.radius == null ? ' · from template' : '');

  const orn = d.ornament == null ? 1 : d.ornament;
  $('dsOrnament').value = Math.round(orn * 100);
  $('dsOrnamentOut').textContent = Math.round(orn * 100) + '%' +
    (d.ornament == null ? ' · from template' : '');

  $('dsReset').disabled = !designIsCustom();
  renderDataMeta();
  $('settingsTplName').textContent = tpl.name;
}

/** One override changed: store it, push it to <html>, redraw the controls. */
export function setDesign(key, value) {
  S.design[key] = value;
  save();
  applyDesign();
  renderSettings();
}

export function initSettings() {
  /* the drawer is the third occupant of the right-hand slot */
  $('settingsToggle').addEventListener('click', () =>
    setDrawer(document.body.classList.contains('settings-open') ? null : 'settings'));
  $('settingsClose').addEventListener('click', () => setDrawer(null));

  const fontOptions = (sel) => {
    sel.appendChild(el('option', null, 'From template')).value = '';
    FONT_CHOICES.forEach((f) => { sel.appendChild(el('option', null, f.name)).value = f.key; });
  };
  fontOptions($('dsHeading'));
  fontOptions($('dsBody'));

  $('dsHeading').addEventListener('change', (e) => setDesign('headingFont', e.target.value || null));
  $('dsBody').addEventListener('change',    (e) => setDesign('bodyFont',    e.target.value || null));
  $('dsAccent').addEventListener('input',   (e) => setDesign('accent',      e.target.value));
  $('dsRadius').addEventListener('input',   (e) => setDesign('radius',      +e.target.value));
  $('dsOrnament').addEventListener('input', (e) => setDesign('ornament',    +e.target.value / 100));

  $('dsAccentClear').addEventListener('click', () => setDesign('accent', null));

  $('dsReset').addEventListener('click', () => {
    S.design = DESIGN_DEFAULTS();
    save(); applyDesign(); renderSettings();
    onRepaint();
    toast('Design reset to the template');
  });

  initDataControls();
  applyTemplate(S.ui.template, false);
}

/* ── export and restore ──
   The only two buttons in the app that touch the whole of state at once. */

/** Set just before the reload that completes a restore, to stop the
 *  save-on-unload handler overwriting what was just restored. */
export let leavingForRestore = false;

export function initDataControls() {
  $('dataExport').addEventListener('click', () => {
    /* The pending save is flushed first. `save()` is debounced by 180ms so the
       horizon does not lag a keystroke, which means the last thing typed may
       still be in memory and not in storage — and a backup taken from storage
       would silently lack it. */
    try { storage().write(KEYS.main, JSON.stringify(S)); } catch { /* reported below */ }

    const at = new Date();
    const blob = new Blob([exportAll(at)], { type: 'application/json' });
    const a = el('a');
    a.href = URL.createObjectURL(blob);
    a.download = backupFilename(at);
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    toast(`Saved ${a.download} to your downloads.`);
  });

  $('dataImport').addEventListener('click', () => $('dataImportFile').click());

  $('dataImportFile').addEventListener('change', async (e) => {
    const file = e.target.files && e.target.files[0];
    // Cleared so choosing the same file twice in a row still fires a change.
    e.target.value = '';
    if (!file) return;

    const found = inspectBackup(await file.text());
    if (found.ok !== true) return toast(found.why, true);

    askConfirm(
      'Replace everything with this backup?',
      `${describeBackup(found.summary)} Restoring overwrites the term you have `
        + 'open now, and that cannot be undone — export a backup of it first if '
        + 'you might want it.',
      'Replace everything',
      () => {
        try {
          restoreBackup(found.parsed);
        } catch (err) {
          return toast(`Could not restore: ${/** @type {Error} */ (err).message}`, true);
        }
        // Reloading is the honest way to do this: every module read state at
        // boot, and re-deriving all of it in place is a far larger surface for
        // getting it half-right than simply starting again.
        leavingForRestore = true;
        location.reload();
      });
  });

  renderDataMeta();
}

/** What is in this browser, so "export" is not a leap of faith. */
export function renderDataMeta() {
  const meta = $('dataMeta');
  if (!meta) return;
  const n = (v) => (Array.isArray(v) ? v.length : 0);
  meta.textContent = `Holding ${n(S.tasks)} task${n(S.tasks) === 1 ? '' : 's'}, `
    + `${n(S.courses)} course${n(S.courses) === 1 ? '' : 's'} and `
    + `${n(S.deadlines)} deadline${n(S.deadlines) === 1 ? '' : 's'}.`;
}
